import type { JobInfo } from "./api.ts";
import { buildDownloadFilename } from "./download.ts";

type SelectableJob = Pick<JobInfo, "id" | "state" | "model_key" | "result">;

export interface DownloadEntry {
  jobId: string;
  filename: string;
  storageKey: string;
  /** Stored size in bytes, when known; used to keep each ZIP a manageable size. */
  size?: number;
}

/** The server accepts at most this many files in one ZIP request. */
export const ZIP_MAX_ENTRIES = 1000;
/** Each ZIP is held in browser memory before saving, so large selections are split. */
export const ZIP_MAX_BYTES = 500 * 1024 * 1024;

/** Only finished generations with stored output can be selected for bulk actions. */
export function isSelectable(job: Pick<JobInfo, "state" | "result">): boolean {
  return job.state === "completed" && Boolean(job.result.assets?.some((asset) => asset.storage_key));
}

export function selectableIds(jobs: SelectableJob[]): string[] {
  return jobs.filter(isSelectable).map((job) => job.id);
}

export function toggleId(ids: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Keep only ids still on screen and still selectable; returns the same set when nothing changed. */
export function retainIds(ids: ReadonlySet<string>, jobs: SelectableJob[]): ReadonlySet<string> {
  if (ids.size === 0) return ids;
  const allowed = new Set(selectableIds(jobs));
  const next = new Set([...ids].filter((id) => allowed.has(id)));
  return next.size === ids.size ? ids : next;
}

/** Every stored output of every selected generation, in on-screen order, named like the single Download button. */
export function downloadPlan(jobs: SelectableJob[], ids: ReadonlySet<string>): DownloadEntry[] {
  const entries: DownloadEntry[] = [];
  for (const job of jobs) {
    if (!ids.has(job.id) || !isSelectable(job)) continue;
    (job.result.assets ?? []).forEach((asset, index) => {
      if (!asset.storage_key) return;
      entries.push({ jobId: job.id, storageKey: asset.storage_key, filename: buildDownloadFilename(job.model_key, job.id, index, asset.mime_type), size: asset.size });
    });
  }
  return entries;
}

/** Split a download plan into ZIP parts, in order, each within the entry and byte limits. A single oversized file gets its own part. */
export function zipParts(entries: DownloadEntry[], maxEntries = ZIP_MAX_ENTRIES, maxBytes = ZIP_MAX_BYTES): DownloadEntry[][] {
  const parts: DownloadEntry[][] = [];
  let current: DownloadEntry[] = [];
  let bytes = 0;
  for (const entry of entries) {
    const size = Number.isFinite(entry.size) && entry.size! > 0 ? entry.size! : 0;
    if (current.length && (current.length >= maxEntries || bytes + size > maxBytes)) {
      parts.push(current);
      current = [];
      bytes = 0;
    }
    current.push(entry);
    bytes += size;
  }
  if (current.length) parts.push(current);
  return parts;
}

/** `pandafilms-selection.zip`, or numbered names when the selection is split. */
export function zipPartName(part: number, parts: number): string {
  return parts > 1 ? `pandafilms-selection-${part}-of-${parts}.zip` : "pandafilms-selection.zip";
}
